import { InjectableClass } from '@fino/lib-injection';
import _ from 'lodash';

export class HistoryService extends InjectableClass {
  constructor(
    $filter,
    HistoryDisplayMode,
    HistoryEventDateFormat,
    HistoryEventTimeFormat,
    HistoryEventDetailsSplitter,
    TicketEventIcons,
    DocumentTypeDetection,
    historyEventNameTemplate,
    historyTimeTemplate
  ) {
    'ngInject';
    super({
      $filter,
      HistoryDisplayMode,
      HistoryEventDateFormat,
      HistoryEventTimeFormat,
      HistoryEventDetailsSplitter,
      TicketEventIcons,
      DocumentTypeDetection,
      historyEventNameTemplate,
      historyTimeTemplate
    });
  }

  get dateFormat() {
    return this.HistoryDateFormat;
  }
  get timeFormat() {
    return this.HistoryTimeFormat;
  }
  get displayMode() {
    return this.HistoryDisplayMode.event.code;
  }
  get displayModeOptions() {
    return Object.values(this.HistoryDisplayMode);
  }

  getEventIcon(event) {
    const { TicketEventIcons } = this;
    return TicketEventIcons[event.eventName] || TicketEventIcons.defaultIcon;
  }

  updateEvent(event) {
    const { $filter } = this;
    event.date = $filter('date')(event.timestamp, this.dateFormat);
    event.time = $filter('date')(event.timestamp, this.timeFormat);
    event.viewType = event.eventName.split('.').pop();

    // Parse event details for specific events
    switch (event.viewType) {
      // Filter out following events
      case 'FinancesSanitized':
        return null;

      // Custom event type handling
      case 'VerifiedFinances':
        this.updateVerifiedFinancesEvent(event);
        break;

      // Transfer succeeded
      case 'BalanceListForCustomerSent':
      case 'BalanceListForCustomerSentViaFax':
      case 'BalanceListRejectionSent':
      case 'BalanceListRejectionSentViaFax':
      case 'CustomerPackageSent':
      case 'CustomerPackageSentViaFax':
      case 'CustomerPackageSentViaMail':
      case 'PartnerInformedViaEmail':
      case 'DocumentSent':
      case 'DocumentSentViaFax':
      case 'OutgoingSignedWarrantRendered':
      case 'OutgoingSignedWarrantRenderedViaFax':
      case 'RejectionSent':
      case 'RejectionSentViaFax':
      case 'ReminderSent':
      case 'ReminderSentViaFax':
      case 'TransactionDataSent':
      case 'TransactionDataSentViaFax':
        this.updateDocumentTransferredEvent(event);
        break;

      // Transfer failed
      case 'BalanceListRejectionTransferFailed':
      case 'BalanceListRejectionTransferFailedViaFax':
      case 'CustomerPackageTransferFailed':
      case 'CustomerPackageTransferFailedViaFax':
      case 'CustomerPackageTransferFailedViaEmail':
      case 'PaymentPartnerNotificationFailed':
      case 'PaymentPartnerNotificationFailedViaFax':
      case 'RejectionDocumentTransferFailed':
      case 'RejectionDocumentTransferFailedViaFax':
      case 'ReminderDocumentTransferFailed':
      case 'ReminderDocumentTransferFailedViaFax':
      case 'SignedWarrantTransferFailed':
      case 'SignedWarrantTransferFailedViaFax':
      case 'TransactionDataTransferFailed':
      case 'TransactionDataTransferFailedViaFax':
      case 'PartnerInformingFailedViaEmail':
        this.updateDocumentTransferFailedEvent(event);
        break;
    }
  }

  /**
   * Expected incoming event details:
   * "directDebits:%d || receivedPayments:%d || scheduledTransactions:%d || standingOrders:%d"
   */
  updateVerifiedFinancesEvent(event) {
    this.parseEventDetails(event);
  }

  /**
   * Expected incoming event details:
   * "partnerName:%s || recipient:%s || shipmentReceipt:%s || sentTimestamp:%s || transactionId:%s"
   */
  updateDocumentTransferredEvent(event) {
    this.parseEventDetails(event);
    event.documentType = this.detectDocumentType(event);
    event.viewType = 'DocumentTransferred';
  }

  /**
   * Expected incoming event details:
   * "partnerName:%s || recipient:%s || shipmentReceipt:%s || timestamp:%s || transactionId:%s || reason:%s"
   */
  updateDocumentTransferFailedEvent(event) {
    this.parseEventDetails(event);
    event.documentType = this.detectDocumentType(event);

    // NOTE: Actual we handle/display this event the same way
    // we handle the updateDocumentTransferredEvent. Therefore
    // we use the same viewType to render the event details.
    event.viewType = 'DocumentTransferred';
  }

  parseEventDetails(event, options = {}) {
    const { HistoryEventDetailsSplitter } = this;
    if (event.details) {
      const lines = event.details.split(HistoryEventDetailsSplitter);
      event.details = _.reduce(lines, (details, line) => {
        let split = line.split(':');
        let key = split[0];
        let value = split[1];

        details[key] = value;

        return details;
      }, {});
    }
  }

  /**
   * Detect the document type by the event name suffix.
   * As fallback for insufficient event names we use the
   * recipient value from the event details to detect
   * the documents type.
   */
  detectDocumentType(event, defaultType = 'letter') {
    let detectedType = defaultType;
    if(event.versandart){
      return event.versandart;
    }
    _.forEach(this.DocumentTypeDetection, (pattern, type) => {
      if (pattern.test(event.eventName)) {
        detectedType = type;
        return false;
      }
    });

    return detectedType;
  }
}
